import { Alert } from 'antd'
import { array, record, semigroup } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import * as React from 'react'
import { InvoiceWithTotal, sortFromSorterResult } from '../../types'
import { FittedTable } from '../styled'
import Upload from '../Upload'
import AddBillModal from './AddBillModal'
import BillsTableHeader from './BillsTableHeader'
import { useBills, useSelectedBills, useSetSelectedBills } from './queries'
import { Query } from './types'
import { useColumns } from './columns'

const billsArrayToRecord = (
  bs: InvoiceWithTotal[]
): Record<string, InvoiceWithTotal> => {
  return record.fromFoldableMap(
    semigroup.first<InvoiceWithTotal>(),
    array.Foldable
  )(bs, b => [b.id.toString(), b])
}

const Bills = () => {
  const [{ page, itemsPerPage, sort }, setReq] = React.useState<Query>({
    page: 0,
    itemsPerPage: 10,
  })
  const { data: billsData, isLoading } = useBills({
    page,
    itemsPerPage,
    sort,
  })
  const { data, count } = billsData ?? { data: [], count: 0 }

  const selectedBills = useSelectedBills()
  const setSelectedBills = useSetSelectedBills()

  const selectBills = (ids: React.Key[]) => {
    const allBills = { ...selectedBills, ...billsArrayToRecord(data) }
    const newBills = pipe(
      ids,
      array.map(id => (typeof id === 'string' ? id : id.toString())),
      array.map(id => allBills[id]),
      billsArrayToRecord
    )

    setSelectedBills(newBills)
  }

  const [hoveredID, setHoveredID] = React.useState<number | undefined>(
    undefined
  )

  const columns = useColumns(sort, hoveredID)

  return (
    <Alert.ErrorBoundary>
      <Upload />
      <AddBillModal />
      <FittedTable<InvoiceWithTotal & { key: string }>
        dataSource={data.map(b => ({ ...b, key: `${b.id}` }))}
        title={() => <BillsTableHeader />}
        columns={columns}
        rowSelection={{
          selectedRowKeys: record.keys(selectedBills),
          onChange: selectBills,
          getCheckboxProps: item => ({ name: item.status }),
          columnWidth: '48px',
        }}
        onRow={b => ({
          onMouseEnter: () => setHoveredID(b.id),
          onMouseLeave: () =>
            b.id === hoveredID ? setHoveredID(undefined) : undefined,
        })}
        loading={isLoading}
        pagination={{ pageSize: itemsPerPage, current: page + 1, total: count }}
        size="middle"
        onChange={(q, _, sorter) => {
          setReq(p => ({
            page: (q.current ?? p.page + 1) - 1,
            itemsPerPage: q.pageSize ?? p.itemsPerPage,
            sort: sortFromSorterResult<
              keyof InvoiceWithTotal,
              InvoiceWithTotal & { key: string }
            >(sorter),
          }))
        }}
        scroll={{ y: '' }}
      />
    </Alert.ErrorBoundary>
  )
}

export default Bills
